import { Ticket, TicketUpdated } from "@/types/ticket";
import { Message } from '@/components/inbox/types';
import { HttpClient } from "./http";
import { BackendTicketResponse } from "@/utils/mappers/ticketMapper";
import api from "@/services/api";
import { getAuthToken } from "@/utils/auth/tokenManager";
export interface UpdateTicketResponse {
  status: string;
  message: string;
  data: Ticket;
}

export interface GetTicketResponse {
  status: string;
  message: string;
  data: Ticket;
}

export interface GetAllTicketsResponse {
  status: string;
  message: string;
  data: { docs: Ticket[] };
}

export interface GetConversationResponse {
  status: string;
  message: string;
  data: Message[];
}

const API_URL = '/ticket';

/**
 * Service for ticket-related API operations
 */
export const ticketService = {
  /**
   * Get a specific ticket by ID
   */
  getTicket: async (ticketId?: string) => {
    if (!ticketId) {
      throw new Error('Ticket ID is required');
    }
    const response = await HttpClient.apiClient.get(`/ticket/${ticketId}`);
    return response.data;
  },

  // ✅ Get ticket details by SNo 
  async fetchTicketBySno(sno: string | number): Promise<GetTicketResponse> {
    try {
      console.log(`Making GET request to ${API_URL}/${sno}`);
      const response = await HttpClient.apiClient.get<GetTicketResponse>(
        `${API_URL}/${sno}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error fetching ticket with SNo ${sno}:`, error);
      throw new Error(`Failed to fetch ticket with SNo ${sno}`);
    }
  },


  // Direct method to get a ticket's sno without fetching the whole ticket
  async getSno(ticketId: string): Promise<string> {
    try {
      const response = await HttpClient.apiClient.get<GetTicketResponse>(
        `${API_URL}/${ticketId}`
      );

      // Extract the sno from the response
      const snoValue = response.data?.data?.sno;

      if (snoValue) {
        // Convert to string since the API returns it as a number
        return String(snoValue);
      }

      return ticketId; // Fall back to using the ID if sno isn't available
    } catch (error) {
      console.error('Error getting ticket sno:', error);
      return ticketId; // Fall back to using the ID if there's an error
    }
  },

  // ✅ Update a ticket
  async updateTicket(ticket_sno: string | number | { id?: string, sno?: number } | null, ticketData: Partial<Ticket>): Promise<UpdateTicketResponse> {
    let finalIdentifier = '';
    let isId = false;

    try {
      // Remove workspace_id and other unused fields from the body
      const {
        workspace_id,
        id,
        ticket_sno: ticketSno,
        sno,
        hasNotification,
        isUnread,
        assigneeAvatar,
        categories,
        notificationType,
        ...cleanedData
      } = ticketData as any;

      // Handle object input
      if (typeof ticket_sno === 'object' && ticket_sno !== null) {
        finalIdentifier = ticket_sno.sno ? String(ticket_sno.sno) : String(ticket_sno.id || '');
      } else {
        finalIdentifier = String(ticket_sno || '');
      }

      if (!finalIdentifier) {
        throw new Error('Invalid ticket identifier');
      }

      // Check if the identifier is an SNO (positive integer) or ID (UUID)
      const isSno = /^\d+$/.test(finalIdentifier) && parseInt(finalIdentifier) > 0;
      isId = !isSno;

      // Construct the appropriate API endpoint
      const endpoint = isId
        ? `${API_URL}/id/${finalIdentifier}`
        : `${API_URL}/${finalIdentifier}`;

      // Log the full request details for debugging
      console.log(`Making PUT request to ${endpoint} with data:`, cleanedData);
      const response = await HttpClient.apiClient.put<UpdateTicketResponse>(
        endpoint,
        cleanedData
      );

      // Log successful response
      console.log('Update ticket response:', response.data);

      // Validate response format
      if (!response.data || !response.data.data) {
        console.error('Invalid response structure:', response.data);
        throw new Error('Invalid response from server');
      }

      return response.data;
    } catch (error: any) {
      // Enhanced error logging
      console.error(`Error updating ticket with ${isId ? 'ID' : 'SNO'} ${finalIdentifier}:`, error);

      if (error.response) {
        console.error('Response status:', error.response.status);
        console.error('Response data:', error.response.data);
        console.error('Response headers:', error.response.headers);

        if (error.response.status === 404) {
          throw new Error(`Ticket with ${isId ? 'ID' : 'SNO'} ${finalIdentifier} not found.`);
        }
      } else if (error.request) {
        console.error('No response received. Request details:', error.request);
        throw new Error('No response received from server. Check your network connection.');
      } else {
        console.error('Error setting up request:', error.message);
      }

      const errorMessage = error.response?.data?.message || error.message || 'Failed to update ticket';
      throw new Error(errorMessage);
    }
  },

  //get conversation for a ticket
  async getConversation(ticket_sno: string | number | any): Promise<GetConversationResponse> {
    try {
      const response = await HttpClient.apiClient.get<GetConversationResponse>(`${API_URL}/${ticket_sno}/conversation`);
      return response.data;
    } catch (error: any) {
      console.error('Error getting conversation:', error);
      throw new Error('Failed to get conversation');
    }
  },

  /**
   * Add a reply to a ticket
   */
  addReply: async (ticketId: string, content: string) => {
    return HttpClient.apiClient.post(`/ticket/${ticketId}/replies`, { content });
  },

  /**
   * Get last 10 conversations for a ticket
   */
  getTicketConversation: async (ticketId: string) => {
    const response = await HttpClient.apiClient.get(`/ticket/${ticketId}/conversation`);
    return response.data;
  },

  /**
   * Get all tickets
   */
  getTickets: async (params?: { page?: number; limit?: number;[key: string]: any }): Promise<BackendTicketResponse> => {
    // Main ticket endpoint uses 'page' parameter
    const response = await HttpClient.apiClient.get('/ticket', { params });
    return response.data;
  },

  /**
   * Get tickets assigned to a specific user
   */
  getAssignedTickets: async (userId: string, params?: { page?: number; limit?: number }): Promise<BackendTicketResponse> => {
    // Convert page to skip for this endpoint since it uses skip, not page
    const queryParams = params ? {
      skip: params.page ? (params.page - 1) * (params.limit || 5) : 0,
      limit: params.limit || 5
    } : {};

    const response = await HttpClient.apiClient.get(`/ticket/user/${userId}/assigned`, { params: queryParams });
    return response.data;
  },

  /**
   * Get tickets mentioned by a specific user
   */
  getMentionedTickets: async (userId: string, params?: { page?: number; limit?: number }): Promise<BackendTicketResponse> => {
    // Convert page to skip for mentions endpoint
    const queryParams = {
      user_id: userId,
      ...(params ? {
        skip: params.page ? (params.page - 1) * (params.limit || 5) : 0,
        limit: params.limit || 5
      } : {})
    };
    const response = await HttpClient.apiClient.get('/mentions', { params: queryParams });
    return response.data;
  },

  /**
   * Get tickets unassigned to a specific user
   */
  getUnassignedTickets: async (userId: string, params?: { page?: number; limit?: number }): Promise<BackendTicketResponse> => {
    // Convert page to skip for this endpoint since it uses skip, not page
    const queryParams = params ? {
      skip: params.page ? (params.page - 1) * (params.limit || 5) : 0,
      limit: params.limit || 5
    } : { limit: 30 };

    const response = await HttpClient.apiClient.get('/ticket/unassigned', { params: queryParams });
    return response.data;
  },

  /**
   * Get tickets assigned to bots
   */
  getBotTickets: async (params?: { page?: number; limit?: number }): Promise<BackendTicketResponse> => {
    // Bot endpoint supports both page and skip, let's use page for consistency
    const response = await HttpClient.apiClient.get('/ticket/bot', { params });
    return response.data;
  },

  rewriteText: async (ticketId: string, text: string, tool: 'shorten' | 'expand' | 'change_tone', tone: string): Promise<{ text: string }> => {
    try {
      const token = getAuthToken();
      const rewriteBaseUrl = import.meta.env.VITE_MCP_API_URL || 'https://beta-ai.pullseai.com';

      const response = await HttpClient.apiClient.post(
        `${rewriteBaseUrl}/api/ticket/${ticketId}/rewrite-text`,
        { text, tool, tone },
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined
          }
        }
      );

      if (response.status < 200 || response.status >= 300) {
        throw new Error(response?.data?.message || 'Failed to rewrite text');
      }

      const data = response?.data;
      const normalizedText = data?.text ?? data?.data?.text ?? data?.data ?? '';
      return { text: typeof normalizedText === 'string' ? normalizedText : '' };
    } catch (error: any) {
      console.error('Error rewriting text:', error?.response?.data?.message || error?.message || 'Failed to rewrite text');
      return { text: "" };
    }
  },

  summarizeConversation: async (ticketId: string): Promise<{ text: string }> => {
    try {
      const summarizerBaseUrl = import.meta.env.VITE_MCP_API_URL || 'https://beta-ai.pullseai.com';
      const token = getAuthToken();
      const response = await HttpClient.apiClient.get(
        `${summarizerBaseUrl}/api/ticket/${ticketId}/summarise`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined
          }
        }
      );
      const { data } = response;
      if (data?.text) {
        return data;
      }
      if (data?.issue || data?.description) {
        const parts = [
          data.issue ? `Issue: ${data.issue}` : '',
          data.description ? `Description: ${data.description}` : ''
        ].filter(Boolean);
        return { text: parts.join('\n\n') || '' };
      }
      return { text: "" };
    } catch (error: any) {
      console.error('Error summarizing conversation:', error?.response?.data?.message || error?.message || 'Failed to summarize conversation');
      return { text: "" };
    }
  },

  getTicketsByPage: async (params?: { page?: number; limit?: number; sortOrder?: "asc" | "desc", view?: "all" | "unassigned" | "my-inbox", ticketId?: string }): Promise<{
    tickets: TicketUpdated[];
    totalTicketCount: number;
  }> => {
    const response = await HttpClient.apiClient.get('/ticket/list', { params });
    if (response.data && response.data.data) {
      return response.data.data;
    } else {
      throw new Error('Failed to fetch tickets');
    }
  },

  getConversationPaginated: async (ticketId: string, params?: { page?: number; limit?: number }): Promise<{ data: Message[]; pagination: { page: number; limit: number; total: number; hasMore: boolean; hasPrevious: boolean } }> => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());

    const url = `/ticket/id/${ticketId}/conversation${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    const response = await HttpClient.apiClient.get(url);

    // Handle both old and new response formats for backward compatibility
    if (response.data.pagination) {
      return response.data;
    } else {
      // Legacy format - calculate pagination from total and current data
      const messages = response.data.data || response.data;
      const currentPage = params?.page || 1;
      const limit = params?.limit || 5;
      const total = response.data.total || messages.length;

      return {
        data: messages,
        pagination: {
          page: currentPage,
          limit: limit,
          total: total,
          hasMore: (currentPage * limit) < total,
          hasPrevious: currentPage > 1
        }
      };
    }
  }
};
